// Function index:
//   ICON_KEYWORDS       — ordered [keywords[], iconShortName] table (first match wins).
//   LIBRARY_ICONS       — every known icon short-name (keyword targets + the 5 bare
//                         kind names + their literal 'kind-*' filenames — mirrors the
//                         52-entry server ICON_LIBRARY enum, the authoritative contract).
//   iconForItem(item)   — icon path/URI for an item; kind fallback guaranteed.
//     Precedence: valid props.icon_path (drawn, → data URI) > known props.icon
//     (picked from the library) > keyword table match on name > kind fallback.

export const ICON_KEYWORDS: ReadonlyArray<[string[], string]> = [
  // order matters: more specific before more general
  [['crossbow'], 'crossbow'], [['waterskin', 'flask', 'canteen'], 'waterskin'],
  [['dagger', 'knife', 'dirk'], 'dagger'], [['sword', 'blade', 'sabre', 'saber', 'rapier', 'scimitar'], 'sword'],
  [['pickaxe', 'war pick', 'mining pick'], 'pickaxe'], [['axe', 'hatchet'], 'axe'], [['mace', 'morningstar'], 'mace'], [['hammer', 'maul'], 'hammer'],
  [['spear', 'pike', 'lance', 'javelin'], 'spear'], [['bow', 'longbow', 'shortbow'], 'bow'],
  [['staff', 'quarterstaff'], 'staff'], [['wand'], 'wand'], [['sling'], 'sling'],
  [['whip'], 'whip'], [['club', 'cudgel'], 'club'],
  [['shield', 'buckler'], 'shield'], [['cloak', 'cape', 'mantle'], 'cloak'], [['helm', 'helmet'], 'helmet'], // 'cap' hit captain/cape-class words
  [['breastplate', 'cuirass', 'mail', 'plate', 'jerkin', 'vest'], 'breastplate'],
  [['boot', 'shoe', 'greave'], 'boots'], [['gauntlet', 'glove'], 'gauntlet'],
  [['belt', 'girdle'], 'belt'],
  [['elixir', 'tonic'], 'elixir'], [['potion', 'philtre', 'draught', 'vial'], 'potion'],
  [['bread', 'loaf'], 'bread'], [['meat', 'venison', 'jerky', 'ration'], 'meat'],
  [['cheese'], 'cheese'], [['herb', 'leaf', 'root'], 'herb'],
  [['bandage', 'poultice', 'salve'], 'bandage'],
  [['scroll', 'parchment', 'letter'], 'scroll'], [['key'], 'key'],
  [['gem', 'jewel', 'emerald', 'ruby', 'sapphire', 'diamond', 'crystal'], 'gem'],
  // fishing-rod BEFORE fish ('fishing' contains 'fish'); fish BEFORE ring ('herring' contains 'ring')
  [['fishing'], 'fishing-rod'], // bare rod/pole collide with Polearm + Rod- names
  [['herring', 'fish', 'trout', 'salmon', 'eel'], 'meat'], // fish → closest food icon
  [['ring', 'band'], 'ring'], [['amulet', 'pendant', 'necklace', 'talisman', 'charm'], 'amulet'],
  // bare metal adjectives (gold/silver/copper) deliberately NOT keywords —
  // "Golden Idol" should fall to its kind icon, not a coin.
  [['coin', 'purse', 'pieces', 'currency'], 'coin'], [['torch'], 'torch'],
  [['rope', 'cord'], 'rope'], [['lantern', 'lamp'], 'lantern'],
  [['book', 'tome', 'grimoire', 'journal'], 'book'], [['map', 'chart'], 'map'],
  [['quill', 'ink'], 'quill'], [['bone'], 'bone'], [['skull'], 'skull'], // 'pen' hit serpent
  [['feather', 'plume'], 'feather'], [['mushroom', 'fungus', 'toadstool'], 'mushroom'],
  [['flower', 'rose', 'blossom'], 'flower'],
];

const KIND_FALLBACKS = new Set(['weapon', 'armor', 'consumable', 'quest', 'misc']);

export const LIBRARY_ICONS: ReadonlySet<string> = new Set([
  ...ICON_KEYWORDS.map(([, target]) => target),
  ...KIND_FALLBACKS,
  ...[...KIND_FALLBACKS].map((k) => `kind-${k}`),
]);

// Verbatim: a single SVG path `d` attribute charset, no markup. Must stay
// set-equivalent with server/src/gm/tools/items.ts::ICON_PATH_CHARSET.
const PATH_RE = /^[MmLlHhVvCcSsQqTtAaZz0-9 ,.-]+$/;
const MAX_ICON_PATH = 4000;

function customIconUri(d: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path d='${d}'/></svg>`;
  // encodeURIComponent leaves `'` unescaped, which breaks out of the CSS
  // url('...') single-quoted string the sheet wraps this in — every drawn
  // icon would render as an untinted solid square. Escape it explicitly.
  return `data:image/svg+xml,${encodeURIComponent(svg).replace(/'/g, '%27')}`;
}

function libraryIconPath(icon: string): string {
  return `/icons/items/${KIND_FALLBACKS.has(icon) ? `kind-${icon}` : icon}.svg`;
}

export function iconForItem(item: { name: string; kind: string; props?: Record<string, unknown> }): string {
  const { name, kind, props } = item;

  const iconPath = props?.icon_path;
  if (typeof iconPath === 'string' && iconPath.length > 0 && iconPath.length <= MAX_ICON_PATH && PATH_RE.test(iconPath)) {
    return customIconUri(iconPath);
  }

  const iconName = props?.icon;
  if (typeof iconName === 'string' && LIBRARY_ICONS.has(iconName)) {
    return libraryIconPath(iconName);
  }

  const n = name.toLowerCase();
  for (const [keywords, icon] of ICON_KEYWORDS) {
    if (keywords.some((k) => n.includes(k))) return `/icons/items/${icon}.svg`;
  }
  return `/icons/items/kind-${KIND_FALLBACKS.has(kind) ? kind : 'misc'}.svg`;
}
